import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'tsf_access_token';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); },
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const api = axios.create({
  baseURL: API,
  withCredentials: false,  // using Bearer header (ingress strips cookie CORS)
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Pretty error mapper
export function fmtError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export const plansAPI = {
  list: () => api.get('/plans').then(r => r.data),
  get: (id) => api.get(`/plans/${id}`).then(r => r.data),
};

// New product catalogue (Phase 4). Public read + product-based order flow.
export const productsAPI = {
  list: () => api.get('/products').then(r => r.data),
  createOrder: (product_id, pay_currency = 'usdtbsc', promo_code) => {
    const body = { product_id, pay_currency };
    if (promo_code) body.promo_code = promo_code;
    return api.post('/orders/v2', body).then(r => r.data);
  },
};

export const authAPI = {
  me: () => api.get('/auth/me').then(r => r.data),
  login: (body) => api.post('/auth/login', body).then(r => r.data),
  register: (body) => api.post('/auth/register', body).then(r => r.data),
  logout: () => api.post('/auth/logout').then(r => r.data),
  forgot: (body) => api.post('/auth/forgot-password', body).then(r => r.data),
  reset: (body) => api.post('/auth/reset-password', body).then(r => r.data),
};

export const ordersAPI = {
  create: (body) => api.post('/orders', body).then(r => r.data),
  get: (id) => api.get(`/orders/${id}`).then(r => r.data),
  mine: () => api.get('/orders').then(r => r.data),
  currencies: () => api.get('/payments/currencies').then(r => r.data),
};

export const accountsAPI = {
  mine: () => api.get('/accounts').then(r => r.data),
  get: (id) => api.get(`/accounts/${id}`).then(r => r.data),
  requestPhase: (id, note, pay_currency) => api.post(`/accounts/${id}/request-phase-upgrade`, { account_id: id, note, pay_currency }).then(r => r.data),
};

export const adminAPI = {
  stats: () => api.get('/admin/stats').then(r => r.data),
  users: () => api.get('/admin/users').then(r => r.data),
  accounts: () => api.get('/admin/accounts').then(r => r.data),
  orders: () => api.get('/admin/orders').then(r => r.data),
  markOrderPaid: (id) => api.post(`/admin/orders/${id}/mark-paid`).then(r => r.data),
  phaseRequests: (status = 'pending', target = null) => {
    const t = target ? `&target=${target}` : '';
    return api.get(`/admin/phase-requests?status=${status}${t}`).then(r => r.data);
  },
  approvePhase: (id, creds) => api.post(`/admin/phase-requests/${id}/approve`, creds).then(r => r.data),
  denyPhase: (id, reason) => api.post(`/admin/phase-requests/${id}/deny`, { reason }).then(r => r.data),
  resetPhase: (id) => api.post(`/admin/phase-requests/${id}/reset`).then(r => r.data),
  payouts: (status = 'pending') => api.get(`/admin/payouts?status=${status}`).then(r => r.data),
  approvePayout: (id) => api.post(`/admin/payouts/${id}/approve`).then(r => r.data),
  denyPayout: (id, reason) => api.post(`/admin/payouts/${id}/deny`, { reason }).then(r => r.data),
  resetPayout: (id) => api.post(`/admin/payouts/${id}/reset`).then(r => r.data),
  affiliatePayouts: (status = 'pending') => api.get(`/admin/affiliate-payouts?status=${status}`).then(r => r.data),
  approveAffiliate: (id) => api.post(`/admin/affiliate-payouts/${id}/approve`).then(r => r.data),
  markAffiliatePaid: (id) => api.post(`/admin/affiliate-payouts/${id}/mark-paid`).then(r => r.data),
  denyAffiliate: (id, reason) => api.post(`/admin/affiliate-payouts/${id}/deny`, { reason }).then(r => r.data),
  resetAffiliate: (id) => api.post(`/admin/affiliate-payouts/${id}/reset`).then(r => r.data),
  breach: (id, body) => api.post(`/admin/accounts/${id}/breach`, body || {}).then(r => r.data),
  assignCreds: (id, body) => api.post(`/admin/accounts/${id}/assign-credentials`, body).then(r => r.data),
  // Promotions
  promotions: () => api.get('/admin/promotions').then(r => r.data),
  createPromo: (body) => api.post('/admin/promotions', body).then(r => r.data),
  updatePromo: (id, body) => api.patch(`/admin/promotions/${id}`, body).then(r => r.data),
  endPromoNow: (id) => api.post(`/admin/promotions/${id}/end-now`).then(r => r.data),
  deletePromo: (id) => api.delete(`/admin/promotions/${id}`).then(r => r.data),
  createAffiliateCode: (body) => api.post('/admin/promotions/affiliate-code', body).then(r => r.data),
  // PnL
  setPnL: (id, body) => api.patch(`/admin/accounts/${id}/pnl`, body).then(r => r.data),
  // Certificates (view/download all)
  certificates: (params = {}) => api.get('/admin/certificates', { params }).then(r => r.data),
  updateCertificate: (id, body) => api.patch(`/admin/certificates/${id}`, body).then(r => r.data),
  createShowcaseCertificate: (body) => api.post('/admin/certificates/showcase', body).then(r => r.data),
  deleteCertificate: (id) => api.delete(`/admin/certificates/${id}`).then(r => r.data),
  downloadCertificate: async (id) => {
    const res = await api.get(`/admin/certificates/${id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `TSG-Certificate-${id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },
  // User termination
  terminateUser: (uid, reason = '') => api.post(`/admin/users/${uid}/terminate`, { reason }).then(r => r.data),
  reinstateUser: (uid) => api.post(`/admin/users/${uid}/reinstate`).then(r => r.data),
  // Pool inventory (Phase 3)
  poolList: (params = {}) => api.get('/admin/pool', { params }).then(r => r.data),
  poolInventory: () => api.get('/admin/pool/inventory').then(r => r.data),
  poolBulkAdd: (accounts) => api.post('/admin/pool/bulk-add', { accounts }).then(r => r.data),
  poolRetire: (poolId) => api.post(`/admin/pool/${poolId}/retire`).then(r => r.data),
  // Products (Phase 1)
  products: () => api.get('/admin/products').then(r => r.data),
  updateProduct: (id, body) => api.patch(`/admin/products/${id}`, body).then(r => r.data),
  // Account pause / unpause (Phase 3)
  pauseAccount: (id, reason = '') => api.post(`/admin/accounts/${id}/pause`, { reason }).then(r => r.data),
  unpauseAccount: (id, body = null) => api.post(`/admin/accounts/${id}/unpause`, body).then(r => r.data),
};

export const promotionsAPI = {
  active: () => api.get('/promotions/active').then(r => r.data),
  validate: (code, applies_to) =>
    api.post('/promotions/validate', { code, applies_to }).then(r => r.data),
};

export const paymentAgentsAPI = {
  list: (country) => api.get('/payment-agents', { params: country ? { country } : {} }).then(r => r.data),
  apply: (body) => api.post('/payment-agents/apply', body).then(r => r.data),
};

// Admin add-ons for payment agents
export const adminPaymentAgentsAPI = {
  list: () => api.get('/admin/payment-agents').then(r => r.data),
  create: (body) => api.post('/admin/payment-agents', body).then(r => r.data),
  update: (id, body) => api.patch(`/admin/payment-agents/${id}`, body).then(r => r.data),
  remove: (id) => api.delete(`/admin/payment-agents/${id}`).then(r => r.data),
  apps: (status = 'pending') => api.get('/admin/payment-agent-applications', { params: { status } }).then(r => r.data),
  appImage: (id) => api.get(`/admin/payment-agent-applications/${id}/id-image`).then(r => r.data),
  approveApp: (id) => api.post(`/admin/payment-agent-applications/${id}/approve`).then(r => r.data),
  rejectApp: (id, notes = '') => api.post(`/admin/payment-agent-applications/${id}/reject`, { notes }).then(r => r.data),
  deleteApp: (id) => api.delete(`/admin/payment-agent-applications/${id}`).then(r => r.data),
};

export const payoutsAPI = {
  mine: () => api.get('/payouts').then(r => r.data),
  request: (body) => api.post('/payouts', body).then(r => r.data),
};

export const certificatesAPI = {
  mine: () => api.get('/certificates').then(r => r.data),
  publicList: (limit = 24) => api.get('/public/certificates', { params: { limit } }).then(r => r.data),
  download: async (id) => {
    const res = await api.get(`/certificates/${id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `TSG-Certificate-${id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },
};

export const affiliateAPI = {
  me: () => api.get('/affiliate/me').then(r => r.data),
  leaderboard: () => api.get('/affiliate/leaderboard').then(r => r.data),
  request: (body) => api.post('/affiliate/payout-request', body).then(r => r.data),
  myRequests: () => api.get('/affiliate/payout-requests').then(r => r.data),
};